/**
 * HomeOSSetupPage — Onboarding · Welcome to your Residence.
 *
 * Growing Home principle: no fake data. The Residence is built from
 * the resident's own answers during onboarding.
 *
 * Steps:
 *   1. Welcome
 *   2. Household name
 *   3. Family members (+ pets)
 *   4. What matters most? (priorities → rooms to highlight first)
 *   5. Preparing your Residence…
 *   → Today
 *
 * Presentation only — persistence and navigation flow through the
 * HomeOSState context (updateHousehold / completeOnboarding).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import {
  Caption, TextLink, PillButton, ModuleMark, useLightPhase,
} from "./components/os/editorial";

const PRIORITIES = [
  { id: 'meals',     label: 'Meals',     tone: 'meals',     hint: 'What we cook, what we buy' },
  { id: 'money',     label: 'Money',     tone: 'money',     hint: 'Bills, goals and savings' },
  { id: 'home',      label: 'Home',      tone: 'home',      hint: 'Repairs, systems and safety' },
  { id: 'family',    label: 'Family',    tone: 'family',    hint: 'Charter, roles and rhythms' },
  { id: 'health',    label: 'Health',    tone: 'health',    hint: 'Appointments and care plans' },
  { id: 'documents', label: 'Documents', tone: 'documents', hint: 'What we never want to lose' },
  { id: 'shopping',  label: 'Shopping',  tone: 'pantry',    hint: 'Pantry, storage and stock' },
  { id: 'cleaning',  label: 'Cleaning',  tone: 'cleaning',  hint: 'Zones and reset routines' },
];

export default function HomeOSSetupPage() {
  const navigate = useNavigate();
  const { completeOnboarding, household } = useHomeOSState();
  const { phase } = useLightPhase();

  const [step, setStep] = useState(household?.onboarded ? 4 : 0);
  const [householdName, setHouseholdName] = useState(household?.name || "");
  const [address, setAddress] = useState(household?.address || "");
  const [members, setMembers] = useState([{ id: 'draft-0', name: '', role: 'Adult' }]);
  const [petInput, setPetInput] = useState("");
  const [pets, setPets] = useState(household?.pets || []);
  const [priorities, setPriorities] = useState(new Set(household?.priorities || []));

  const advance = () => setStep((s) => Math.min(s + 1, 4));
  const back    = () => setStep((s) => Math.max(s - 1, 0));

  const canAdvance = () => {
    if (step === 1) return householdName.trim().length > 0;
    if (step === 2) return members.some((m) => m.name.trim().length > 0);
    if (step === 3) return priorities.size > 0;
    return true;
  };

  const addMember = () => {
    setMembers((ms) => [...ms, { id: `draft-${ms.length}`, name: '', role: 'Adult' }]);
  };
  const updateMember = (idx, patch) => {
    setMembers((ms) => ms.map((m, i) => i === idx ? { ...m, ...patch } : m));
  };
  const removeMember = (idx) => {
    setMembers((ms) => (ms.length === 1 ? ms : ms.filter((_, i) => i !== idx)));
  };
  const addPet = () => {
    const v = petInput.trim();
    if (!v) return;
    setPets((ps) => [...ps, v]);
    setPetInput("");
  };
  const removePet = (i) => setPets((ps) => ps.filter((_, idx) => idx !== i));

  const togglePriority = (id) => {
    setPriorities((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const finish = () => {
    const cleanMembers = members
      .filter((m) => m.name.trim().length > 0)
      .map((m) => ({ name: m.name.trim(), role: m.role }));
    completeOnboarding({
      name: householdName.trim(),
      address: address.trim(),
      pets,
      priorities: Array.from(priorities),
      members: cleanMembers,
    });
    toast.success(`Welcome to ${householdName.trim()}.`);
    setTimeout(() => navigate('/home-os/welcome'), 600);
  };

  return (
    <div className={`min-h-screen phase-${phase}`} style={{ background: 'var(--ara-canvas)' }} data-testid="home-os-setup-page">
      <div className="max-w-[560px] mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20">
        <ModuleMark module="today" label={`Step ${Math.min(step + 1, 5)} of 5`} className="mb-8" />

        {step === 0 && (
          <div data-testid="setup-step-welcome">
            <h1 className="display-ara text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.05] tracking-[-0.015em] max-w-[16ch]">
              Welcome to your Residence.
            </h1>
            <p className="mt-6 font-display italic text-[17px] sm:text-[19px] leading-[1.4] max-w-[38ch]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
              Every room begins almost empty — waiting for its family.
            </p>
            <p className="mt-6 prose-ara text-[14.5px] max-w-[42ch]">
              A few quiet questions and ARA will prepare a residence that already belongs to your household.
            </p>
            <div className="mt-12">
              <PillButton variant="solid" onClick={advance} testid="setup-welcome-continue">
                Begin →
              </PillButton>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="setup-step-household">
            <h1 className="display-ara text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.015em] max-w-[18ch]">
              What do you call your household?
            </h1>
            <p className="mt-4 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
              Your family name, your address, a nickname — anything that makes the residence yours.
            </p>
            <div className="mt-10">
              <Caption>Household name</Caption>
              <input
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
                placeholder="e.g. The Cook Family"
                className="mt-2 w-full bg-transparent border-b py-3 outline-none text-[22px] leading-tight tracking-[-0.01em]"
                style={{ color: 'var(--ara-ink)', borderColor: 'var(--ara-ink)' }}
                data-testid="setup-household-name-input"
                autoFocus
              />
            </div>
            <div className="mt-8">
              <Caption>Address · optional</Caption>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="A neighbourhood or full address"
                className="mt-2 w-full bg-transparent border-b py-2 outline-none text-[15px]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', borderColor: 'var(--ara-line)' }}
                data-testid="setup-address-input"
              />
            </div>
            <div className="mt-14 flex items-center gap-8">
              <TextLink onClick={back}>Back</TextLink>
              <PillButton variant="solid" onClick={advance} disabled={!canAdvance()} testid="setup-household-continue">
                Continue →
              </PillButton>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="setup-step-family">
            <h1 className="display-ara text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.015em] max-w-[18ch]">
              Who lives here with you?
            </h1>
            <p className="mt-4 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
              Just names and roles. You can add more later.
            </p>

            <div className="mt-10">
              {members.map((m, idx) => (
                <div
                  key={m.id}
                  className={`py-4 ${idx === 0 ? 'border-t' : ''} border-b`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`setup-member-row-${idx}`}
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <input
                      value={m.name}
                      onChange={(e) => updateMember(idx, { name: e.target.value })}
                      placeholder="Name"
                      className="flex-1 min-w-[140px] bg-transparent py-2 outline-none text-[17px] leading-tight tracking-[-0.005em]"
                      style={{ color: 'var(--ara-ink)' }}
                      data-testid={`setup-member-name-${idx}`}
                      autoFocus={idx === 0}
                    />
                    <select
                      value={m.role}
                      onChange={(e) => updateMember(idx, { role: e.target.value })}
                      className="bg-transparent caption-ara focus:outline-none"
                      style={{ color: 'var(--ara-ink)' }}
                      data-testid={`setup-member-role-${idx}`}
                    >
                      <option value="Adult">Adult</option>
                      <option value="Teen">Teen</option>
                      <option value="Child">Child</option>
                      <option value="Baby">Baby</option>
                      <option value="Elder">Elder</option>
                      <option value="Guest">Guest</option>
                    </select>
                    {members.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMember(idx)}
                        className="caption-ara"
                        style={{ color: 'var(--ara-ink-soft)' }}
                        aria-label="Remove"
                        data-testid={`setup-member-remove-${idx}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="mt-4">
                <TextLink onClick={addMember} testid="setup-add-member-btn">
                  Add another person →
                </TextLink>
              </div>
            </div>

            <div className="mt-12">
              <Caption>Pets · optional</Caption>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                {pets.map((p, i) => (
                  <button
                    key={`${p}-${i}`}
                    type="button"
                    onClick={() => removePet(i)}
                    className="caption-ara"
                    style={{ border: '1px solid var(--ara-line)', padding: '4px 10px', borderRadius: '999px', color: 'var(--ara-ink)' }}
                    data-testid={`setup-pet-chip-${i}`}
                  >
                    {p} ×
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <input
                  value={petInput}
                  onChange={(e) => setPetInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPet(); } }}
                  placeholder="e.g. Milo the dog"
                  className="flex-1 bg-transparent border-b py-2 outline-none text-[14px]"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', borderColor: 'var(--ara-line)' }}
                  data-testid="setup-pet-input"
                />
                <TextLink onClick={addPet} testid="setup-add-pet-btn">Add pet</TextLink>
              </div>
            </div>

            <div className="mt-14 flex items-center gap-8">
              <TextLink onClick={back}>Back</TextLink>
              <PillButton variant="solid" onClick={advance} disabled={!canAdvance()} testid="setup-family-continue">
                Continue →
              </PillButton>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="setup-step-priorities">
            <h1 className="display-ara text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.015em] max-w-[18ch]">
              What would you like ARA to help with first?
            </h1>
            <p className="mt-4 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
              Pick as many as you like. The rest will be quietly there when you need them.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="setup-priorities-grid">
              {PRIORITIES.map((p) => {
                const active = priorities.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePriority(p.id)}
                    aria-pressed={active}
                    data-testid={`setup-priority-${p.id}`}
                    className="text-left py-4 flex items-start gap-3 transition-ara"
                    style={{ borderTop: '1px solid var(--ara-line-soft)' }}
                  >
                    <span
                      className="dot-ara mt-1.5 flex-shrink-0"
                      style={{
                        background: active ? `var(--ara-mod-${p.tone === 'today' ? 'home' : p.tone === 'pantry' ? 'pantry' : p.tone})` : 'transparent',
                        border: active ? 'none' : '1px solid var(--ara-line)',
                      }}
                      aria-hidden
                    />
                    <span className="flex-1">
                      <span
                        className="block text-[15.5px] leading-tight tracking-[-0.005em]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: active ? 500 : 400 }}
                      >
                        {p.label}
                      </span>
                      <span className="block caption-ara mt-1" style={{ color: 'var(--ara-ink-soft)' }}>
                        {p.hint}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-14 flex items-center gap-8">
              <TextLink onClick={back}>Back</TextLink>
              <PillButton variant="solid" onClick={advance} disabled={!canAdvance()} testid="setup-priorities-continue">
                Continue →
              </PillButton>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="setup-step-ready">
            <h1 className="display-ara text-[30px] sm:text-[36px] lg:text-[44px] leading-[1.05] tracking-[-0.015em] max-w-[18ch]">
              Your Residence is ready.
            </h1>
            <p className="mt-6 font-display italic text-[17px] sm:text-[19px] leading-[1.4] max-w-[40ch]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
              {householdName ? `Welcome home, ${householdName}.` : "Welcome home."}
            </p>
            <p className="mt-6 prose-ara text-[14.5px] max-w-[42ch]">
              Each room begins almost empty — an invitation, not a dashboard.
              Add a first thing to any room and ARA will begin to compose your household's daily brief.
            </p>
            <div className="mt-14">
              <PillButton variant="solid" onClick={finish} testid="setup-finish-btn">
                Enter the Foyer →
              </PillButton>
              <div className="mt-6">
                <TextLink onClick={back} testid="setup-back-to-edit">Change something</TextLink>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
